import './announcements.css';
import { AnnouncementPlacement } from '@/api/app-state/app-state.types.ts';
import { AnnouncementBanner } from './announcement-banner.component.tsx';
import { AnnouncementPopup } from './announcement-popup.component.tsx';
import { useEffectiveAnnouncements } from './use-effective-announcements.ts';

interface Props {
  /** Which app surface this host renders for. */
  placement: AnnouncementPlacement;
  /** Optional cap on the number of stacked banners (highest priority first). */
  maxBanners?: number;
  /** Extra class on the banner stack (e.g. shell-specific padding). */
  className?: string;
}

/**
 * Renders the effective banners and the top-priority popup for one surface.
 * Mount once per shell (external portal, internal admin, login). The backend
 * already sorts by priority, so we take banners in order and the first popup.
 */
export function AnnouncementsHost({
  placement,
  maxBanners = 3,
  className = '',
}: Props) {
  const { banners, popups, dismiss } = useEffectiveAnnouncements(placement);

  const visibleBanners = banners.slice(0, maxBanners);
  const activePopup = popups[0];

  if (visibleBanners.length === 0 && !activePopup) {
    return null;
  }

  return (
    <>
      {visibleBanners.length > 0 && (
        <div className={`eos-ann-stack ${className}`.trim()}>
          {visibleBanners.map((a) => (
            <AnnouncementBanner
              key={a.id}
              announcement={a}
              onDismiss={dismiss}
            />
          ))}
        </div>
      )}
      {activePopup && (
        <AnnouncementPopup announcement={activePopup} onClose={dismiss} />
      )}
    </>
  );
}
